import { handleActions } from "redux-actions";
import { CATALOG, SCOPE_BRAND_SET } from "constants/action-types";
import _ from "lodash";

export interface INamespace {
    error?: Error;
    isFetching: boolean;
    results: string[];
    cacheKey: string;
    nextPage?: string;
    hasMoreResults: boolean;
    isBulkDeleting: boolean;
    refreshingFilters: boolean;
}

export interface IState {
    collections: {
        [namespace: string]: INamespace;
    };
}

export const initialState: IState = {
    collections: {},
};

export const getDefaultReducerState = (cacheKey?) => ({
    cacheKey,
    results: [],
    error: undefined,
    isFetching: true,
    hasMoreResults: false,
    nextPage: undefined,
    isBulkDeleting: false,
    refreshingFilters: false,
});

export const entriesReducer = handleActions<IState, any>(
    {
        [SCOPE_BRAND_SET]: () => {
            return initialState;
        },
        [CATALOG.ENTRIES.FETCH.START]: (state: IState, { meta }: any) => {
            if (
                !meta.forceUpdate &&
                meta.cacheKey &&
                state.collections[meta.key] &&
                state.collections[meta.key].cacheKey === meta.cacheKey
            ) {
                return state;
            }
            if (meta.nextPage) {
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [meta.key]: {
                            ...state.collections[meta.key],
                            isFetching: true,
                            hasMoreResults: false,
                            nextPage: undefined,
                        },
                    },
                };
            }
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.key]: getDefaultReducerState(meta.cacheKey),
                },
            };
        },
        [CATALOG.ENTRIES.FETCH.COMPLETED]: (state: IState, { error, meta, payload }: any) => {
            const current = state.collections[meta.key];
            if (error) {
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [meta.key]: {
                            ...current,
                            error: payload,
                            isFetching: false,
                            hasMoreResults: false,
                            nextPage: undefined,
                        },
                    },
                };
            }
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.key]: {
                        ...current,
                        results: meta.replace ? payload.results : (current?.results || []).concat(payload.results),
                        nextPage: payload.nextPage,
                        hasMoreResults: !!payload.nextPage,
                        isFetching: false,
                    },
                },
            };
        },
        [CATALOG.ENTRIES.CACHE.INVALIDATE]: (state: IState, { payload }: any) => {
            const collectionState = state.collections[payload] || { results: [] };
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [payload]: {
                        ...collectionState,
                        cacheKey: "",
                    },
                },
            };
        },
        [CATALOG.ENTRY.ADD.FETCH.START]: (state: IState, { meta, payload }: any) => {
            if (payload.index !== undefined && !meta.alreadyAdded) {
                const current = state.collections[payload.catalogId];
                const results = state.collections[payload.catalogId].results.slice(0); // clone
                results.splice(payload.index, 0, payload.entry.id);
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [payload.catalogId]: {
                            ...current,
                            error: undefined,
                            results,
                        },
                    },
                };
            } else {
                return state;
            }
        },
        [CATALOG.ENTRY.ADD.FETCH.COMPLETED]: (state: IState, { payload, meta, error }: any) => {
            const current = state.collections[meta.catalogId];
            if (error) {
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [meta.catalogId]: {
                            ...current,
                            error: payload,
                            isFetching: false,
                            hasMoreResults: false,
                            nextPage: undefined,
                        },
                    },
                };
            }

            if (payload.syncId !== undefined) {
                const results = current.results.slice(0);
                const index = results.indexOf(payload.syncId);
                if (index !== -1) {
                    results[index] = payload.results;
                    return {
                        ...state,
                        collections: {
                            ...state.collections,
                            [meta.catalogId]: {
                                ...current,
                                refreshingFilters: true,
                                results,
                                isFetching: false,
                            },
                        },
                    };
                } else {
                    return state;
                }
            }

            if (!_.isNil(payload.rejectedSyncId)) {
                const results = _.reject(current.results, (id) => id === payload.rejectedSyncId);
                return {
                    ...state,
                    collections: {
                        ...state.collections,
                        [meta.catalogId]: {
                            ...current,
                            results,
                            isFetching: false,
                        },
                    },
                };
            }
            return state;
        },
        [CATALOG.ENTRIES.REORDER.COMPLETED]: (state: IState, { payload, meta }: any) => {
            const current = state.collections[meta.catalogId];
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        error: undefined,
                        results: payload.results,
                    },
                },
            };
        },
        [CATALOG.ENTRY.DELETE.START]: (state: IState, { payload, meta }: any) => {
            const current = state.collections[meta.catalogId];
            const results = current.results.slice(0);
            const index = results.indexOf(payload.entry.id);
            results.splice(index, 1);
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        error: undefined,
                        results,
                    },
                },
            };
        },
        [CATALOG.ENTRIES.DELETE.PRUNE]: (state: IState, { payload, meta }: any) => {
            const current = state.collections[meta.catalogId];
            let results = current.results;
            let isBulkDeleting = false;
            if (payload.id) {
                results = results.filter((entry) => !payload.id.includes(entry));
            } else {
                results = results.filter((entry) => meta.entries[entry].type === "separator");
                isBulkDeleting = true;
            }
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        error: undefined,
                        results,
                        isBulkDeleting,
                    },
                },
            };
        },
        [CATALOG.ENTRIES.DELETE.COMPLETED]: (state: IState, { meta }: any) => {
            const current = state.collections[meta.catalogId];
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        refreshingFilters: true,
                        isBulkDeleting: false,
                    },
                },
            };
        },
        [CATALOG.ENTRIES.FILTERS.REFRESH.START]: (state: IState, { meta }: any) => {
            const current = state.collections[meta.catalogId];
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        refreshingFilters: true,
                    },
                },
            };
        },
        [CATALOG.ENTRIES.FILTERS.REFRESH.COMPLETE]: (state: IState, { meta }: any) => {
            const current = state.collections[meta.catalogId];
            return {
                ...state,
                collections: {
                    ...state.collections,
                    [meta.catalogId]: {
                        ...current,
                        refreshingFilters: false,
                    },
                },
            };
        },
    },
    initialState
);
